import { useMemo, lazy, Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import Loading from '@/components/shared/Loading'
import { useAppSelector } from '@/store'
import {
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_SIMPLE,
    LAYOUT_TYPE_DECKED,
    LAYOUT_TYPE_BLANK,
} from '@/constants/theme.constant'
import useAuth from '@/features/base/user-management/auth/hooks/useAuth'
import useIdleLogout from '@/hooks/useIdleLogout'
import useDirection from '@/hooks/useDirection'
import useLocale from '@/hooks/useLocale'
import { getMatchedRoute } from '@/utils/route'

const layouts = {
    [LAYOUT_TYPE_CLASSIC]: lazy(() => import('./ClassicLayout')),
    [LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
    [LAYOUT_TYPE_SIMPLE]: lazy(() => import('./SimpleLayout')),
    [LAYOUT_TYPE_DECKED]: lazy(() => import('./DeckedLayout')),
    [LAYOUT_TYPE_BLANK]: lazy(() => import('./BlankLayout')),
}

const Layout = () => {
    // Auto log-out the user after 15 minutes of inactivity
    useIdleLogout()
    const defaultLayoutType = useAppSelector((state) => state.theme.layout.type)
    const location = useLocation()
    const { authenticated } = useAuth()

    useDirection()
    useLocale()

    // Get the matched route for the current path
    const matchedRoute = getMatchedRoute(location.pathname)

    // Use layout from route meta if available, otherwise use the default layout
    const layoutType = matchedRoute?.meta?.layout || defaultLayoutType

    const AppLayout = useMemo(() => {
        if (authenticated) {
            return layouts[layoutType] || layouts[LAYOUT_TYPE_CLASSIC]
        }
        return lazy(() => import('./AuthLayout'))
    }, [layoutType, authenticated])

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            {AppLayout && <AppLayout />}
        </Suspense>
    )
}

export default Layout
